import { useCallback, useState, type MouseEvent } from "react";
import { omit } from "lodash";

import type { MenuItemDef } from "@covertree/types";
import { IconButton } from "@covertree/ui/buttons/IconButton";
import { MoreVert as MoreVertIcon } from "@mui/icons-material";
import { Box, Menu, MenuItem, TooltipProps } from "@mui/material";
import type { BoxProps } from "@mui/material/Box";

type Props = {
  actionMenuItems: MenuItemDef[];
  border?: boolean;
  center?: boolean;
  disabled?: boolean;
  closeOnClick?: boolean;
  wrapperProps?: BoxProps;
  tooltip?: string;
  tooltipPlacement?: TooltipProps["placement"];
};

export function MeatballMenu({
  actionMenuItems,
  border,
  center,
  closeOnClick,
  disabled,
  wrapperProps,
  tooltip,
  tooltipPlacement,
}: Props) {
  const [open, setOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);

  const handleClick = useCallback((event: MouseEvent) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    setAnchorEl(null);
  }, []);

  return (
    <Box
      id="meatball-button-wrapper"
      sx={[
        !center && { alignSelf: "flex-start" },
        !!border && {
          backgroundSize: "contain",
        },
        ...(wrapperProps?.sx && Array.isArray(wrapperProps?.sx)
          ? wrapperProps.sx
          : [wrapperProps?.sx]),
      ]}
      {...omit(wrapperProps, "sx")}>
      <IconButton
        aria-label="Options"
        onClick={handleClick}
        size="large"
        disabled={disabled}
        tooltip={tooltip}
        tooltipPlacement={tooltipPlacement}>
        <MoreVertIcon
          sx={[
            border
              ? (theme) => ({
                  margin: theme.spacing(0.5),
                  backgroundSize: "contain",
                })
              : {},
          ]}
        />
      </IconButton>
      <Menu
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}>
        {actionMenuItems.map(({ label, onClick }) => (
          <MenuItem
            key={label}
            onClick={() => {
              onClick();
              if (closeOnClick) handleClose();
            }}>
            {label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}
